// The scheduling pages' API client (ported from BookMe). Signed-in calls carry the company's Cognito ID token; the
// API works out the company from the token and the host, never from the path.
import { idToken, signIn, signOutLocal } from "../lib/auth";

export const browserTz = () => Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string,
  ) {
    super(message);
  }
}

async function request<T>(method: string, path: string, body?: unknown, auth = true): Promise<T> {
  const headers: Record<string, string> = { "x-timezone": browserTz() };
  if (body !== undefined) headers["content-type"] = "application/json";
  if (auth) {
    const token = await idToken();
    if (!token) {
      signOutLocal();
      void signIn({ back: location.pathname + location.search });
      throw new ApiError(401, "Signing in…");
    }
    headers.authorization = `Bearer ${token}`;
  }
  const res = await fetch(path, { method, headers, body: body === undefined ? undefined : JSON.stringify(body) });
  const data = await res.json().catch(() => ({}));
  if (res.status === 401 && auth) {
    signOutLocal();
    void signIn({ back: location.pathname + location.search });
    throw new ApiError(401, "Signing in…");
  }
  if (!res.ok) throw new ApiError(res.status, data?.error ?? `Request failed (${res.status})`, data?.code);
  return data as T;
}

export const api = {
  get: <T>(p: string) => request<T>("GET", p),
  post: <T>(p: string, b: unknown = {}) => request<T>("POST", p, b),
  /** The eBookMe API updates with POST. */
  put: <T>(p: string, b: unknown) => request<T>("POST", p, b),
  del: <T>(p: string) => request<T>("DELETE", p),
};

export const publicApi = {
  get: <T>(p: string) => request<T>("GET", p, undefined, false),
  post: <T>(p: string, b: unknown) => request<T>("POST", p, b, false),
};

// ---- shared shapes (mirror services/api/src/lib/sched/types.ts) ----
export type ConferenceType = "teams" | "webex" | "phone";
export type Weekday = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";
export type WeeklyHours = Partial<Record<Weekday, { start: string; end: string }[]>>;

/** The signed-in person's scheduling profile. */
export interface Me {
  sub: string;
  email: string;
  name: string;
  timezone: string;
  phoneDialIn?: string;
  phonePasscode?: string;
  webexRoomUrl?: string;
}

export interface Connection {
  id: string;
  provider: "google" | "microsoft";
  accountEmail: string;
  accountName?: string;
  msKind?: "work" | "personal" | "external";
  busyCalendarIds: string[];
  status: "ok" | "error";
  lastError?: string;
}

export interface CalendarInfo {
  id: string;
  name: string;
  primary: boolean;
  canWrite: boolean;
}

export interface Link {
  slug: string;
  ownerSub: string;
  title: string;
  description?: string;
  durationMinutes: number;
  durationOptions?: number[];
  reminderMinutes?: number[];
  slotIntervalMinutes: number;
  bufferBeforeMinutes: number;
  bufferAfterMinutes: number;
  minNoticeMinutes: number;
  maxDaysAhead: number;
  timezone: string;
  weeklyHours: WeeklyHours;
  hostSubs: string[];
  hosts: { sub: string; name: string; email: string }[];
  destination: { connectionId: string; calendarId: string };
  conferenceType: ConferenceType;
  active: boolean;
  /** Turned off because the company's plan allows fewer links. */
  pausedByPlan?: boolean;
  url?: string;
}

export interface Booking {
  id: string;
  linkSlug: string;
  linkTitle: string;
  ownerSub: string;
  hostSubs: string[];
  start: string;
  end: string;
  inviteeName: string;
  inviteeEmail: string;
  inviteeTimezone: string;
  inviteeNotes?: string;
  additionalGuests: string[];
  conference: { type: ConferenceType; joinUrl?: string; dialIn?: string; passcode?: string };
  status: "confirmed" | "canceled";
  cancelReason?: string;
  history?: { at: string; action: string; by: string; detail?: string }[];
  hosts?: { name: string }[];
}

export interface PublicLink {
  slug: string;
  title: string;
  description?: string;
  durationMinutes: number;
  durationOptions?: number[];
  timezone: string;
  maxDaysAhead: number;
  conferenceType: ConferenceType;
  hosts: { name: string }[];
}

/** Connections that can receive bookings (outside-organization accounts are availability only). */
export const canReceiveBookings = (c: Connection) => c.msKind !== "external";

/** "30 min", "1 hr", "1 hr 30 min". */
export const fmtMinutes = (m: number) => {
  const h = Math.floor(m / 60);
  const r = m % 60;
  return h ? `${h} hr${r ? ` ${r} min` : ""}` : `${m} min`;
};

/** The lengths a link offers (older links only have durationMinutes). */
export const durationsOf = (l: { durationMinutes: number; durationOptions?: number[] }) => (l.durationOptions?.length ? l.durationOptions : [l.durationMinutes]);

export const conferenceLabel: Record<ConferenceType, string> = {
  teams: "Microsoft Teams",
  webex: "Webex",
  phone: "Conference call",
};
